import { z } from 'zod';
import { resourceNameSchema } from './tools';

/**
 * Skills folder import, in the Agent Skills layout used by Claude Code (`~/.claude/skills`,
 * `.claude/skills`) and Claude apps: one folder per skill with a `SKILL.md` —
 *
 *   ---
 *   name: pdf-processing
 *   description: Extract text and tables from PDFs. Use when …
 *   ---
 *   # Instructions …
 *
 * The body becomes the skill's instructions. The folder's other text files (references, forms,
 * scripts) are kept with the skill: agents see their list and read one when needed with the
 * built-in `read_skill_file` tool, so large references never bloat every prompt. Binary files are
 * left out.
 */

export const MAX_SKILL_INSTRUCTIONS = 20_000;
export const MAX_SKILL_FILE_BYTES = 64 * 1024;
/** One reference file, and all of a skill's reference files together. */
export const MAX_SKILL_REFERENCE_BYTES = 256 * 1024;
export const MAX_SKILL_REFERENCES_TOTAL = 1024 * 1024;
export const MAX_SKILL_REFERENCES = 100;

/** Extensions read as text references (anything else is reported as skipped). */
export const SKILL_TEXT_EXTENSIONS = new Set([
  'md',
  'mdx',
  'txt',
  'rst',
  'json',
  'jsonl',
  'yaml',
  'yml',
  'toml',
  'ini',
  'cfg',
  'conf',
  'csv',
  'tsv',
  'xml',
  'html',
  'htm',
  'css',
  'sql',
  'graphql',
  'py',
  'js',
  'mjs',
  'cjs',
  'ts',
  'tsx',
  'jsx',
  'sh',
  'bash',
  'zsh',
  'ps1',
  'rb',
  'go',
  'rs',
  'java',
  'kt',
  'swift',
  'php',
  'r',
  'lua',
  'pl',
  'tex',
  'svg',
  'env',
  'example',
  'template',
  'tmpl',
  'j2',
  'hbs',
  'liquid',
]);
/** Whether a path looks like a text file worth keeping (`LICENSE`, `Makefile`, … count too). */
export function isSkillTextFile(path: string): boolean {
  const name = path.split('/').at(-1) ?? path;
  const ext = name.includes('.') ? name.split('.').at(-1)!.toLowerCase() : '';
  return ext
    ? SKILL_TEXT_EXTENSIONS.has(ext)
    : /^(license|makefile|dockerfile|readme|notice)$/i.test(name);
}

export const skillReferenceSchema = z.object({
  /** Relative to the skill folder, e.g. `reference/forms.md`. */
  path: z
    .string()
    .min(1)
    .max(500)
    .refine((p) => !p.startsWith('/') && !p.split('/').includes('..'), { message: 'Invalid path' }),
  content: z.string().max(MAX_SKILL_REFERENCE_BYTES),
});
export type SkillReference = z.infer<typeof skillReferenceSchema>;

export const skillFileSchema = z.object({
  /** Path of the SKILL.md inside the selected folder, e.g. `skills/pdf/SKILL.md`. */
  path: z.string().min(1).max(1000),
  content: z.string().max(MAX_SKILL_FILE_BYTES),
  /** The skill folder's other text files, kept with the skill. */
  references: z
    .array(skillReferenceSchema)
    .max(MAX_SKILL_REFERENCES)
    .default([])
    .refine(
      (refs) => refs.reduce((n, r) => n + r.content.length, 0) <= MAX_SKILL_REFERENCES_TOTAL,
      {
        message: 'A skill’s reference files are limited to 1 MB in total',
      },
    ),
  /** Files left out in the browser (binary or too large), for the report. */
  skippedFiles: z
    .array(z.object({ path: z.string().max(1000), reason: z.string().max(200) }))
    .max(500)
    .default([]),
});
export type SkillFile = z.infer<typeof skillFileSchema>;

export const importSkillsRequestSchema = z.object({
  skills: z.array(skillFileSchema).min(1, 'No SKILL.md files found').max(100),
  /** What to do when a skill with the same name exists. */
  onConflict: z.enum(['skip', 'update']).default('skip'),
  dryRun: z.boolean().default(false),
});
export type ImportSkillsRequest = z.infer<typeof importSkillsRequestSchema>;

export const skillImportItemSchema = z.object({
  path: z.string(),
  name: z.string(),
  description: z.string(),
  /** Characters of instructions. */
  size: z.number().int(),
  /** Reference files kept with the skill. */
  files: z.array(z.object({ path: z.string(), size: z.number().int() })),
  status: z.enum(['create', 'update', 'created', 'updated', 'skip']),
  reason: z.string().nullable(),
  warnings: z.array(z.string()),
});
export type SkillImportItem = z.infer<typeof skillImportItemSchema>;

export const importSkillsResultSchema = z.object({
  dryRun: z.boolean(),
  items: z.array(skillImportItemSchema),
});
export type ImportSkillsResult = z.infer<typeof importSkillsResultSchema>;

// --- Parsing ---

const unquote = (v: string) => {
  const t = v.trim();
  if (t.length >= 2 && ((t[0] === '"' && t.at(-1) === '"') || (t[0] === "'" && t.at(-1) === "'"))) {
    const inner = t.slice(1, -1);
    return t[0] === '"'
      ? inner.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\')
      : inner.replace(/''/g, "'");
  }
  return t;
};

/**
 * Reads the top-level scalar keys of a YAML frontmatter block: `key: value`, quoted values, and
 * `|` / `>` block scalars. Nested maps and lists are skipped (their keys are reported as present).
 */
export function parseFrontmatter(yaml: string): Record<string, string> {
  const out: Record<string, string> = {};
  const lines = yaml.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const m = /^([A-Za-z_][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (!m) continue;
    const key = m[1]!;
    const raw = m[2]!.replace(/\s+#.*$/, '');
    const block = /^([|>])[+-]?\s*$/.exec(raw);
    if (block || raw === '') {
      // Indented continuation: a block scalar, or a nested map/list we do not need.
      const body: string[] = [];
      while (
        i + 1 < lines.length &&
        (/^\s+\S/.test(lines[i + 1]!) || lines[i + 1]!.trim() === '')
      ) {
        body.push(lines[++i]!);
      }
      const indent = Math.min(
        ...body.filter((b) => b.trim()).map((b) => b.length - b.trimStart().length),
      );
      const text = body.map((b) => b.slice(Number.isFinite(indent) ? indent : 0));
      if (block) {
        out[key] =
          block[1] === '|'
            ? text.join('\n').trim()
            : text
                .join('\n')
                .trim()
                .replace(/([^\n])\n(?!\n)/g, '$1 ');
      } else {
        out[key] = text.join('\n').trim() ? '[nested]' : '';
      }
      continue;
    }
    out[key] = unquote(raw);
  }
  return out;
}

/** A folder or file name as a skill name: `PDF Tools` → `pdf-tools`. */
export function toSkillName(value: string): string {
  const name = value
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .replace(/-+$/g, '')
    .slice(0, 64);
  return name || 'skill';
}

export type ParsedSkill =
  | { ok: true; name: string; description: string; instructions: string; warnings: string[] }
  | { ok: false; name: string; error: string };

/** The folder that holds a SKILL.md (`a/b/SKILL.md` → `b`). */
export const skillFolderName = (path: string) => {
  const parts = path.split('/').filter(Boolean);
  return parts.length >= 2 ? parts.at(-2)! : (parts[0] ?? 'skill');
};

/** Parses one SKILL.md. Never throws. */
export function parseSkillMarkdown(path: string, content: string): ParsedSkill {
  const folder = skillFolderName(path);
  const text = content.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const warnings: string[] = [];
  let meta: Record<string, string> = {};
  let body = text;
  const fm = /^---\n([\s\S]*?)\n---[ \t]*(?:\n|$)/.exec(text);
  if (fm) {
    meta = parseFrontmatter(fm[1]!);
    body = text.slice(fm[0].length);
  } else {
    warnings.push('No frontmatter: the name comes from the folder');
  }

  const declared = meta.name?.trim();
  let name = declared ? toSkillName(declared) : toSkillName(folder);
  if (!resourceNameSchema.safeParse(name).success) name = toSkillName(folder);
  if (declared && name !== declared) warnings.push(`Name “${declared}” is stored as “${name}”`);

  let description = (meta.description ?? '').trim();
  if (!description) {
    warnings.push('No description: agents see only the instructions');
  } else if (description.length > 2000) {
    description = `${description.slice(0, 1999)}…`;
    warnings.push('The description was shortened to 2,000 characters');
  }
  if (meta['allowed-tools'])
    warnings.push('“allowed-tools” is not imported: attach platform tools to the skill instead');

  const instructions = body.trim();
  if (!instructions) return { ok: false, name, error: 'SKILL.md has no instructions' };
  if (instructions.length > MAX_SKILL_INSTRUCTIONS)
    return {
      ok: false,
      name,
      error: `The instructions are ${instructions.length.toLocaleString('en')} characters (limit ${MAX_SKILL_INSTRUCTIONS.toLocaleString('en')})`,
    };
  return { ok: true, name, description, instructions, warnings };
}
